import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import cortexAvatar from "@/assets/cortex-ai-avatar.png";

type Msg = { role: "user" | "assistant"; content: string };

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/website-chat`;

const STARTER_CHIPS_ANONYMOUS = [
  "What services do you offer?",
  "How does Cortex AI work?",
  "I need to talk to someone",
];

const STARTER_CHIPS_LOGGED_IN = [
  "How's my project going?",
  "What's in my portal?",
  "I have a question about my plan",
];

async function streamChat({
  messages,
  onDelta,
  onDone,
  onLeadCaptured,
  sessionId,
  userInfo,
  pageUrl,
}: {
  messages: Msg[];
  onDelta: (text: string) => void;
  onDone: () => void;
  onLeadCaptured?: () => void;
  sessionId: string;
  userInfo?: { user_id?: string; email?: string; name?: string; company_name?: string; website?: string; phone?: string; user_type?: string };
  pageUrl?: string;
}) {
  const resp = await fetch(CHAT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
    },
    body: JSON.stringify({ messages, session_id: sessionId, user_info: userInfo, page_url: pageUrl }),
  });

  if (!resp.ok) {
    if (resp.status === 429 || resp.status === 402) {
      const data = await resp.json();
      throw new Error(data.error || "Service temporarily unavailable");
    }
    // Try to parse JSON error
    try {
      const data = await resp.json();
      if (data.content) {
        // Non-streaming fallback response (lead capture)
        if (data.leadCaptured) onLeadCaptured?.();
        onDelta(data.content);
        onDone();
        return;
      }
      throw new Error(data.error || "Failed to start chat");
    } catch {
      throw new Error("Failed to connect to chat service");
    }
  }

  // Check for lead capture header
  if (resp.headers.get("X-Lead-Captured") === "true") {
    onLeadCaptured?.();
  }

  // Check if response is JSON (non-streaming fallback)
  const contentType = resp.headers.get("Content-Type") || "";
  if (contentType.includes("application/json")) {
    const data = await resp.json();
    if (data.leadCaptured) onLeadCaptured?.();
    onDelta(data.content || "");
    onDone();
    return;
  }

  if (!resp.body) throw new Error("No response body");

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let textBuffer = "";
  let streamDone = false;

  while (!streamDone) {
    const { done, value } = await reader.read();
    if (done) break;
    textBuffer += decoder.decode(value, { stream: true });

    let newlineIndex: number;
    while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
      let line = textBuffer.slice(0, newlineIndex);
      textBuffer = textBuffer.slice(newlineIndex + 1);

      if (line.endsWith("\r")) line = line.slice(0, -1);
      if (line.startsWith(":") || line.trim() === "") continue;
      if (!line.startsWith("data: ")) continue;

      const jsonStr = line.slice(6).trim();
      if (jsonStr === "[DONE]") {
        streamDone = true;
        break;
      }

      try {
        const parsed = JSON.parse(jsonStr);
        const content = parsed.choices?.[0]?.delta?.content as string | undefined;
        if (content) onDelta(content);
      } catch {
        textBuffer = line + "\n" + textBuffer;
        break;
      }
    }
  }

  // Final flush
  if (textBuffer.trim()) {
    for (let raw of textBuffer.split("\n")) {
      if (!raw) continue;
      if (raw.endsWith("\r")) raw = raw.slice(0, -1);
      if (raw.startsWith(":") || raw.trim() === "") continue;
      if (!raw.startsWith("data: ")) continue;
      const jsonStr = raw.slice(6).trim();
      if (jsonStr === "[DONE]") continue;
      try {
        const parsed = JSON.parse(jsonStr);
        const content = parsed.choices?.[0]?.delta?.content as string | undefined;
        if (content) onDelta(content);
      } catch {
        /* ignore */
      }
    }
  }

  onDone();
}

export default function ChatWidget() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [userProfile, setUserProfile] = useState<any>(null);

  // Stable session ID for the lifetime of this widget mount
  const sessionId = useMemo(() => crypto.randomUUID(), []);

  // Fetch user profile for personalization
  useEffect(() => {
    if (!user) { setUserProfile(null); return; }
    const fetchProfile = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      if (data) setUserProfile(data);
    };
    fetchProfile();
  }, [user?.id]);

  const firstName = userProfile?.full_name?.split(" ")[0] || user?.user_metadata?.full_name?.split(" ")[0] || "";

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || isLoading) return;

      const userMsg: Msg = { role: "user", content: text.trim() };
      const updatedMessages = [...messages, userMsg];
      setMessages(updatedMessages);
      setInput("");
      setIsLoading(true);

      let assistantSoFar = "";
      const upsertAssistant = (chunk: string) => {
        assistantSoFar += chunk;
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === "assistant") {
            return prev.map((m, i) =>
              i === prev.length - 1 ? { ...m, content: assistantSoFar } : m
            );
          }
          return [...prev, { role: "assistant", content: assistantSoFar }];
        });
      };

      try {
        await streamChat({
          messages: updatedMessages,
          onDelta: (chunk) => upsertAssistant(chunk),
          onDone: () => {
            setIsLoading(false);
            // Fire-and-forget: update conversation log with assistant response
            if (assistantSoFar) {
              const fullMessages = [...updatedMessages, { role: "assistant" as const, content: assistantSoFar }];
              fetch(CHAT_URL, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
                },
                body: JSON.stringify({
                  messages: fullMessages,
                  session_id: sessionId,
                  user_info: user ? {
                    user_id: user.id,
                    email: user.email,
                    name: userProfile?.full_name || user.user_metadata?.full_name || user.user_metadata?.name || undefined,
                  } : undefined,
                  page_url: window.location.pathname,
                  log_only: true,
                }),
              }).catch(() => {});
            }
          },
          onLeadCaptured: () => {
            toast({
              title: "Contact info received! ✅",
              description:
                "Our team will reach out to you shortly.",
            });
          },
          sessionId,
          userInfo: user ? {
            user_id: user.id,
            email: user.email,
            name: userProfile?.full_name || user.user_metadata?.full_name || user.user_metadata?.name || undefined,
            company_name: userProfile?.company_name || undefined,
            website: userProfile?.website || undefined,
            phone: userProfile?.phone || undefined,
            user_type: userProfile?.user_type || undefined,
          } : undefined,
          pageUrl: window.location.pathname,
        });
      } catch (e: any) {
        console.error("Chat error:", e);
        setIsLoading(false);
        upsertAssistant(
          e.message ||
            "Sorry, I'm having trouble connecting right now. Please try again or contact us at sales@ciwebgroup.com or 877-839-1122."
        );
      }
    },
    [messages, isLoading, sessionId, user, userProfile]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  return (
    <>
      {/* Floating bubble */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-accent shadow-lg shadow-accent/30 hover:shadow-accent/50 transition-shadow"
            aria-label="Open chat"
          >
            <MessageCircle className="h-6 w-6 text-accent-foreground" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed bottom-4 right-4 z-50 flex flex-col w-[calc(100vw-2rem)] max-w-[400px] h-[min(600px,calc(100vh-2rem))] rounded-2xl border border-border bg-card shadow-2xl overflow-hidden sm:bottom-6 sm:right-6"
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-secondary/50">
              <img
                src={cortexAvatar}
                alt="Cortex AI"
                className="h-8 w-8 rounded-full ring-2 ring-accent/40"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">
                  Cortex AI
                </p>
                <p className="text-xs text-muted-foreground">
                  CI Web Group Assistant
                </p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-lg hover:bg-muted transition-colors"
                aria-label="Close chat"
              >
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-4 py-3 space-y-3"
            >
              {messages.length === 0 && (
                <div className="space-y-3">
                  <div className="flex gap-2 items-start">
                    <img
                      src={cortexAvatar}
                      alt=""
                      className="h-6 w-6 rounded-full mt-0.5 flex-shrink-0"
                    />
                    <div className="bg-secondary rounded-xl rounded-tl-sm px-3 py-2 text-sm text-foreground">
                      {firstName ? (
                        <>👋 Welcome back, <strong>{firstName}</strong>! I'm Cortex, your CI Web Group assistant. I have access to your account info — ask me about your portal, projects, plan, or anything else I can help with!</>
                      ) : (
                        <>👋 Hey there! I'm Cortex, CI Web Group's AI assistant. I can help you learn about our SEO, content marketing, website, and AI search optimization services for home services businesses. How can I help?</>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 pl-8">
                    {(firstName ? STARTER_CHIPS_LOGGED_IN : STARTER_CHIPS_ANONYMOUS).map((chip) => (
                      <button
                        key={chip}
                        onClick={() => sendMessage(chip)}
                        className="text-xs px-3 py-1.5 rounded-full border border-accent/30 text-accent hover:bg-accent/10 transition-colors"
                      >
                        {chip}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex gap-2 items-start ${
                    msg.role === "user" ? "justify-end" : ""
                  }`}
                >
                  {msg.role === "assistant" && (
                    <img
                      src={cortexAvatar}
                      alt=""
                      className="h-6 w-6 rounded-full mt-0.5 flex-shrink-0"
                    />
                  )}
                  <div
                    className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                      msg.role === "user"
                        ? "bg-accent text-accent-foreground rounded-tr-sm"
                        : "bg-secondary text-foreground rounded-tl-sm"
                    }`}
                  >
                    {msg.role === "assistant" ? (
                      <div className="prose prose-sm prose-invert max-w-none [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0.5 [&_a]:text-accent [&_a]:underline [&_a]:cursor-pointer [&_strong]:text-foreground">
                        <ReactMarkdown
                          components={{
                            a: ({ href, children, ...props }) => {
                              const isInternal = href?.startsWith("/");
                              if (isInternal) {
                                return (
                                  <a
                                    {...props}
                                    href={href}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      navigate(href!);
                                      setIsOpen(false);
                                    }}
                                    className="text-accent underline cursor-pointer hover:text-accent/80"
                                  >
                                    {children}
                                  </a>
                                );
                              }
                              return (
                                <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                                  {children}
                                </a>
                              );
                            },
                          }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      msg.content
                    )}
                  </div>
                </div>
              ))}

              {isLoading && messages[messages.length - 1]?.role === "user" && (
                <div className="flex gap-2 items-start">
                  <img
                    src={cortexAvatar}
                    alt=""
                    className="h-6 w-6 rounded-full mt-0.5 flex-shrink-0"
                  />
                  <div className="bg-secondary rounded-xl rounded-tl-sm px-3 py-2">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-border p-3">
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask me anything about our services..."
                  rows={1}
                  className="flex-1 resize-none rounded-xl border border-border bg-input px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-accent max-h-24"
                />
                <button
                  onClick={() => sendMessage(input)}
                  disabled={!input.trim() || isLoading}
                  className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent text-accent-foreground disabled:opacity-40 hover:bg-accent/90 transition-colors flex-shrink-0"
                  aria-label="Send message"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
